import dayjs from "dayjs";
import type { Point } from "highcharts";
import { renderToStaticMarkup } from "react-dom/server";

import { fullMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter } from "app/filters/ngFilters";
import CoreTrendCell from "pro-features/components/core cells/src/CoreTrendCell/CoreTrendCell";

type ChangeGrowth = "Growing" | "Stagnating" | "Declining";

type ChangeValue = {
    change: number;
    growth: ChangeGrowth;
};

type TrendPeriodData = {
    /** @format date-time */
    date: string;
    /** @format int64 */
    volume: number;
    monthlyVolumeChange?: ChangeValue;
};

type TrendData = {
    currentPeriod: TrendPeriodData;
    previousPeriod?: TrendPeriodData;
};

type TrendDataT = TrendData[];

const volumeFormatter = (value: number) => abbrNumberFilter()(value);

const TrendChartTooltip = ({ date, volume }: { date: string; volume: number }) => {
    return (
        <Flex
            gap="8px"
            padding="16px"
            justifyContent="space-between"
            background={colorsPalettes.carbon[0]}
            boxShadow="0 0 8px 0 rgba(0, 0, 0, 0.12), 0 8px 8px 0 rgba(0, 0, 0, 0.24)"
            borderRadius="3px"
        >
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[500]}
            >
                {dayjs(date).format(fullMonthDayAndYear)}
            </Text>
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[500]}
            >
                {volumeFormatter(volume)}
            </Text>
        </Flex>
    );
};

const getTrendChartConfig = (data: TrendDataT) => ({
    chart: {
        height: 56,
    },
    tooltip: {
        enabled: true,
        borderWidth: 0,
        outside: true,
        useHTML: true,
        shadow: false,
        backgroundColor: "transparent",
        style: {
            fontFamily: "Roboto",
        },
        formatter(this: Point) {
            return renderToStaticMarkup(
                <TrendChartTooltip
                    volume={this.y ?? 0}
                    date={data[this.key].currentPeriod.date}
                />,
            );
        },
    },
});

type TopicsDiscoverTrendCardChartProps = {
    trendData: TrendDataT;
};

export const TopicsDiscoverTrendCardChart = ({ trendData }: TopicsDiscoverTrendCardChartProps) => {
    const data = trendData ? [{ data: trendData.map((trend) => trend.currentPeriod.volume) }] : [];

    return (
        <CoreTrendCell
            data={data}
            config={getTrendChartConfig(trendData)}
        />
    );
};
